import { Link, NavLink, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import Logo, { LogoMark } from "@/components/app/Logo";
import LangCountrySelector from "@/components/app/LangCountrySelector";
import NotificationBell from "@/components/app/NotificationBell";
import VerifiedBadge from "@/components/app/VerifiedBadge";
import {
  LogOut, MapPin, Calendar, MessageSquare, FileText, Users, Pill, Package, Home,
  Briefcase, GraduationCap, ShieldCheck, UserCircle, Menu, ChevronLeft, UsersRound, Shield, Building2, Handshake, CreditCard,
} from "lucide-react";

const NAV_BY_ROLE = (user) => {
  const uid = user?.user_id;
  const role = user?.role;
  const profileItem = uid ? { to: `/u/${uid}`, icon: UserCircle, label: "Mon profil" } : null;
  if (role === "admin" || user?.is_admin) {
    return [
      { to: "/dashboard/admin/moderation", icon: Shield, label: "Modération" },
      { to: "/dashboard/admin/leads", icon: Handshake, label: "Leads partenaires" },
      { to: "/dashboard/feed", icon: Home, label: "Fil d'actualités" },
      profileItem,
    ].filter(Boolean);
  }
  if (role === "patient") {
    return [
      { to: "/dashboard/feed", icon: Home, label: "Fil d'actualités" },
      { to: "/dashboard/directory", icon: Building2, label: "Annuaire national" },
      { to: "/dashboard/search", icon: MapPin, label: "Trouver un praticien" },
      { to: "/dashboard/groups", icon: UsersRound, label: "Communautés santé" },
      { to: "/dashboard/appointments", icon: Calendar, label: "Mes rendez-vous" },
      { to: "/dashboard/messages", icon: MessageSquare, label: "Messagerie sécurisée" },
      { to: "/dashboard/network", icon: Users, label: "Mes praticiens suivis" },
      { to: "/dashboard/dmp", icon: FileText, label: "Mon DMP" },
      { to: "/dashboard/orders", icon: Package, label: "Mes commandes" },
      profileItem,
    ].filter(Boolean);
  }
  if (role === "doctor") {
    return [
      { to: "/dashboard/feed", icon: Home, label: "Fil d'actualités" },
      { to: "/dashboard/directory", icon: Building2, label: "Annuaire national" },
      { to: "/dashboard/network", icon: Users, label: "Réseau confrères" },
      { to: "/dashboard/groups", icon: UsersRound, label: "Communautés santé" },
      { to: "/dashboard/agenda", icon: Calendar, label: "Mon agenda" },
      { to: "/dashboard/encaissements", icon: CreditCard, label: "Encaissements" },
      { to: "/dashboard/patients", icon: Users, label: "Ma patientèle" },
      { to: "/dashboard/prescriptions", icon: Pill, label: "Ordonnances" },
      { to: "/dashboard/dmp", icon: FileText, label: "DMP partagés" },
      { to: "/dashboard/messages", icon: MessageSquare, label: "Messagerie sécurisée" },
      { to: "/dashboard/jobs", icon: Briefcase, label: "Offres d'emploi" },
      { to: "/dashboard/courses", icon: GraduationCap, label: "Formation continue" },
      { to: "/dashboard/verifications", icon: ShieldCheck, label: "Mes vérifications" },
      profileItem,
    ].filter(Boolean);
  }
  // pharmacy
  return [
    { to: "/dashboard/feed", icon: Home, label: "Fil d'actualités" },
    { to: "/dashboard/directory", icon: Building2, label: "Annuaire national" },
    { to: "/dashboard/groups", icon: UsersRound, label: "Communautés santé" },
    { to: "/dashboard/orders", icon: Package, label: "Commandes reçues" },
    { to: "/dashboard/messages", icon: MessageSquare, label: "Messagerie sécurisée" },
    { to: "/dashboard/network", icon: Users, label: "Mon réseau" },
    profileItem,
  ].filter(Boolean);
};

const ROLE_LABEL = { patient: "Patient", doctor: "Praticien", pharmacy: "Pharmacie", admin: "Administrateur" };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const items = NAV_BY_ROLE(user);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sb_collapsed") === "1");
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    localStorage.setItem("sb_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  useEffect(() => {
    if (!user) return;
    let stop = false;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/conversations");
        if (stop) return;
        const total = data.reduce((acc, c) => {
          const lm = c.last_message;
          if (lm && lm.sender_id !== user.user_id) {
            const seen = localStorage.getItem(`seen_${c.conversation_id}`);
            if (!seen || new Date(seen) < new Date(lm.created_at)) return acc + 1;
          }
          return acc;
        }, 0);
        setUnread(total);
      } catch { /* silent */ }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => { stop = true; clearInterval(id); };
  }, [user]);

  const onLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className={`hidden md:flex ${collapsed ? "w-16" : "w-64"} flex-col border-e border-slate-200 bg-white transition-all duration-200`} data-testid="sidebar">
        <div className={`flex items-center ${collapsed ? "justify-center px-2" : "justify-between px-5"} py-5 border-b border-slate-200`}>
          {collapsed ? <LogoMark size={32} /> : <Link to="/dashboard"><Logo h={28} /></Link>}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`${collapsed ? "absolute -end-3 top-7" : ""} w-7 h-7 rounded-full bg-white border border-slate-200 hover:border-[#618DB8] hover:text-[#618DB8] text-slate-500 flex items-center justify-center shadow-sm transition`}
            data-testid="sidebar-toggle"
            title={collapsed ? "Déplier" : "Replier"}
          >
            {collapsed ? <Menu className="h-3.5 w-3.5" /> : <ChevronLeft className="h-3.5 w-3.5" />}
          </button>
        </div>
        {!collapsed && (
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{ROLE_LABEL[user?.role]}</div>
            <div className="text-sm font-bold text-deep flex items-center mt-0.5 truncate">
              {user?.name}
              {user?.blue_check && <VerifiedBadge size={12} />}
            </div>
          </div>
        )}
        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} data-testid={`nav-${it.to.split("/").pop()}`}
              title={collapsed ? it.label : undefined}
              className={({ isActive }) =>
                `flex items-center ${collapsed ? "justify-center px-2" : "gap-3 px-3"} py-2.5 rounded-lg text-sm font-semibold transition ${
                  isActive ? "bg-[#618DB8]/10 text-[#618DB8]" : "text-slate-600 hover:bg-slate-100"
                }`}>
              <it.icon className="h-4 w-4 flex-shrink-0" />
              {!collapsed && <span className="truncate">{it.label}</span>}
              {!collapsed && it.to === "/dashboard/messages" && unread > 0 && (
                <span className="ms-auto bg-[#FF7A5C] text-white text-[10px] font-bold rounded-full px-1.5 min-w-5 h-5 flex items-center justify-center">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        {!collapsed && (
          <div className="border-t border-slate-200 p-3 space-y-2">
            <LangCountrySelector compact />
            <Button variant="ghost" onClick={onLogout} className="w-full justify-start text-slate-600 text-sm" data-testid="logout-btn">
              <LogOut className="h-4 w-4 me-2" />{t("common.logout")}
            </Button>
          </div>
        )}
        {collapsed && (
          <div className="border-t border-slate-200 p-2 flex flex-col items-center gap-2">
            <Button size="icon" variant="ghost" onClick={onLogout} title="Déconnexion" data-testid="logout-btn-collapsed">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        )}
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        {/* Top utility bar (desktop) — bell + messages */}
        <div className="hidden md:flex items-center justify-end gap-2 px-4 py-2 border-b border-slate-200 bg-white">
          <NotificationBell />
          <Link to="/dashboard/messages" title="Messagerie sécurisée" data-testid="topbar-messages" className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg hover:bg-slate-100 text-slate-600 hover:text-[#618DB8] transition">
            <MessageSquare className="h-5 w-5" />
            {unread > 0 && (
              <span className="absolute -top-1 -end-1 bg-[#FF7A5C] text-white text-[10px] font-bold rounded-full px-1.5 min-w-4 h-4 flex items-center justify-center">{unread}</span>
            )}
          </Link>
        </div>
        <header className="md:hidden border-b border-slate-200 bg-white px-4 py-3 flex items-center justify-between">
          <Link to="/dashboard"><Logo h={26} /></Link>
          <div className="flex items-center gap-1">
            <NotificationBell />
            <Link to="/dashboard/messages" className="relative w-8 h-8 inline-flex items-center justify-center text-slate-600">
              <MessageSquare className="h-5 w-5" />
              {unread > 0 && (<span className="absolute -top-0.5 -end-0.5 bg-[#FF7A5C] text-white text-[10px] font-bold rounded-full px-1 min-w-4 h-4 flex items-center justify-center">{unread}</span>)}
            </Link>
            <LangCountrySelector compact />
            <Button size="sm" variant="ghost" onClick={onLogout}><LogOut className="h-4 w-4" /></Button>
          </div>
        </header>
        <nav className="md:hidden border-b border-slate-200 bg-white px-2 py-2 flex gap-1 overflow-x-auto">
          {items.map((it) => (
            <NavLink key={it.to} to={it.to} data-testid={`m-nav-${it.to.split("/").pop()}`}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-1.5 rounded text-xs font-semibold ${isActive ? "bg-[#618DB8]/10 text-[#618DB8]" : "text-slate-600"}`}>
              {it.label}
            </NavLink>
          ))}
        </nav>
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
